import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, LockKeyhole, Plus } from "lucide-react";

const nowMonth = () => new Date().toISOString().slice(0, 7);
const monthRange = (month) => {
  const [year, monthIndex] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  return {
    start: `${month}-01`,
    end: `${month}-${String(lastDay).padStart(2, "0")}`,
  };
};
const EMPTY_RULE = {
  name: "",
  grace_minutes: 10,
  late_threshold_minutes: 30,
  early_logout_minutes: 15,
  half_day_working_minutes: 240,
  weekly_offs: [5, 6],
  break_allowance_minutes: 60,
  overtime_requires_approval: true,
  is_default: false,
  status: "active",
};

export default function AttendanceManagement() {
  const { user } = useAuth();
  const [month, setMonth] = useState(nowMonth());
  const [rules, setRules] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [locks, setLocks] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [summary, setSummary] = useState([]);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [regularizationOpen, setRegularizationOpen] = useState(false);
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rule, setRule] = useState(EMPTY_RULE);
  const [holiday, setHoliday] = useState({
    date: "",
    name: "",
    location: "all",
  });
  const [regularization, setRegularization] = useState({
    work_date: "",
    proposed_login_at: "",
    proposed_logout_at: "",
    reason: "",
  });
  const [overtimeForm, setOvertimeForm] = useState({
    work_date: "",
    hours: "",
    reason: "",
  });
  const [shiftLink, setShiftLink] = useState({
    shift_id: "",
    attendance_rule_id: "",
  });
  const [lockReason, setLockReason] = useState("");

  const canManage = ["super_admin", "founder", "hr", "hr_admin"].includes(
    user?.role,
  );
  const canLock = ["super_admin", "founder"].includes(user?.role);
  const range = useMemo(() => monthRange(month), [month]);

  const load = useCallback(async () => {
    try {
      const [
        rulesResponse,
        holidaysResponse,
        regularizationResponse,
        overtimeResponse,
        summaryResponse,
      ] = await Promise.all([
        api.get("/hrms/attendance-rules"),
        api.get("/hrms/holidays", { params: range }),
        api.get("/hrms/attendance-regularizations"),
        api.get("/hrms/overtime/requests"),
        api.get("/hrms/payroll/attendance-summary", { params: range }),
      ]);
      setRules(rulesResponse.data.items || []);
      setHolidays(holidaysResponse.data.items || []);
      setRegularizations(regularizationResponse.data.items || []);
      setOvertime(overtimeResponse.data.items || []);
      setSummary(summaryResponse.data.items || []);
      if (canManage) {
        const [shiftResponse, lockResponse] = await Promise.all([
          api.get("/hrms/shifts"),
          api.get("/hrms/attendance-locks"),
        ]);
        setShifts(shiftResponse.data.items || []);
        setLocks(lockResponse.data.items || []);
      }
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, [canManage, range]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (path, payload, success) => {
    setSaving(true);
    try {
      await api.post(path, payload);
      toast.success(success);
      load();
      return true;
    } catch (error) {
      toast.error(apiErr(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createRule = async () => {
    if (
      await submit("/hrms/attendance-rules", rule, "Attendance rule created")
    ) {
      setRuleOpen(false);
      setRule(EMPTY_RULE);
    }
  };

  const createHoliday = async () => {
    if (await submit("/hrms/holidays", holiday, "Holiday added")) {
      setHolidayOpen(false);
      setHoliday({ date: "", name: "", location: "all" });
    }
  };

  const createRegularization = async () => {
    if (
      await submit(
        "/hrms/attendance-regularizations",
        regularization,
        "Regularization submitted",
      )
    ) {
      setRegularizationOpen(false);
      setRegularization({
        work_date: "",
        proposed_login_at: "",
        proposed_logout_at: "",
        reason: "",
      });
    }
  };

  const createOvertime = async () => {
    if (
      await submit(
        "/hrms/overtime/requests",
        {
          ...overtimeForm,
          hours: Number(overtimeForm.hours),
        },
        "Overtime request submitted",
      )
    ) {
      setOvertimeOpen(false);
      setOvertimeForm({ work_date: "", hours: "", reason: "" });
    }
  };

  const linkShift = async () => {
    if (!shiftLink.shift_id || !shiftLink.attendance_rule_id) return;
    setSaving(true);
    try {
      await api.put(`/hrms/shifts/${shiftLink.shift_id}/attendance-rule`, {
        attendance_rule_id: shiftLink.attendance_rule_id,
      });
      toast.success("Shift linked to attendance rule");
      setShiftLink({ shift_id: "", attendance_rule_id: "" });
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleLock = async () => {
    const locked = locks.find((item) => item.month === month)?.locked;
    if (!lockReason.trim()) {
      toast.error("Provide a reason for changing the attendance lock");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/hrms/attendance-locks/${month}`, {
        locked: !locked,
        reason: lockReason,
      });
      toast.success(
        !locked ? "Attendance month locked" : "Attendance month unlocked",
      );
      setLockReason("");
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (decision, details) => {
    const base =
      details.kind === "regularization"
        ? `/hrms/attendance-regularizations/${details.id}/decision`
        : `/hrms/overtime/requests/${details.id}/decision`;
    if (await submit(base, decision, `Request ${decision.decision}`))
      setDecisionTarget(null);
  };

  const locked = locks.find((item) => item.month === month)?.locked;
  return (
    <div className="space-y-6" data-testid="attendance-management-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/hrms"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="attendance-management-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> People & HRMS
          </Link>
          <h1 className="mt-3 font-display text-3xl font-bold">
            Attendance & time controls
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Rules, exceptions, break overages, approvals, and payroll-ready
            attendance totals.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setRegularizationOpen(true)}
            data-testid="attendance-regularization-button"
          >
            <CalendarClock className="mr-1.5 h-4 w-4" /> Regularize attendance
          </Button>
          <Button
            onClick={() => setOvertimeOpen(true)}
            data-testid="attendance-overtime-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            Request overtime
          </Button>
        </div>
      </div>

      <Card className="p-5" data-testid="attendance-summary-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-display text-lg font-semibold">
              Payroll-ready monthly summary
            </div>
            <div className="text-sm text-muted-foreground">
              Present, LOP, approved leave, and approved overtime in one
              controlled source.
            </div>
          </div>
          <Field label="Month">
            <Input
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
              data-testid="attendance-summary-month-input"
            />
          </Field>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">Employee</th>
                <th>Expected</th>
                <th>Present</th>
                <th>Leave</th>
                <th>LOP</th>
                <th>Approved OT</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((item) => (
                <tr
                  key={item.user_id}
                  className="border-b last:border-0"
                  data-testid={`attendance-summary-row-${item.user_id}`}
                >
                  <td className="py-3 font-medium">{item.employee_name}</td>
                  <td>{item.expected_working_days}</td>
                  <td>{item.present_days}</td>
                  <td>{item.approved_leave_days}</td>
                  <td>{item.lop_days}</td>
                  <td>{item.approved_overtime_hours}h</td>
                </tr>
              ))}
              {!summary.length && (
                <tr>
                  <td colSpan={6} className="py-6 text-muted-foreground">
                    No attendance summary in scope.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {canManage && (
        <div className="grid gap-5 xl:grid-cols-[1.3fr_1fr]">
          <Card className="p-5" data-testid="attendance-rules-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold">
                  Attendance rules
                </div>
                <div className="text-sm text-muted-foreground">
                  Rules are snapshotted when attendance starts.
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setRuleOpen(true)}
                data-testid="attendance-rule-create-button"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Add rule
              </Button>
            </div>
            <div className="mt-4 grid gap-3">
              {rules.map((item) => (
                <RuleRow key={item.id} item={item} />
              ))}
            </div>
          </Card>
          <Card className="p-5" data-testid="attendance-shift-link-card">
            <div className="font-display text-lg font-semibold">
              Link shifts to rules
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              The linked rule controls grace, breaks, weekly offs, and
              thresholds.
            </div>
            <div className="mt-4 grid gap-3">
              <Select
                value={shiftLink.shift_id}
                onValueChange={(shift_id) =>
                  setShiftLink({ ...shiftLink, shift_id })
                }
              >
                <SelectTrigger data-testid="attendance-shift-link-select">
                  <SelectValue placeholder="Select shift" />
                </SelectTrigger>
                <SelectContent>
                  {shifts.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={shiftLink.attendance_rule_id}
                onValueChange={(attendance_rule_id) =>
                  setShiftLink({ ...shiftLink, attendance_rule_id })
                }
              >
                <SelectTrigger data-testid="attendance-rule-link-select">
                  <SelectValue placeholder="Select rule" />
                </SelectTrigger>
                <SelectContent>
                  {rules.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={linkShift}
                disabled={
                  saving || !shiftLink.shift_id || !shiftLink.attendance_rule_id
                }
                data-testid="attendance-shift-link-submit-button"
              >
                Link shift
              </Button>
            </div>
          </Card>
        </div>
      )}

      {canManage && (
        <HolidayAndLockPanel
          holidays={holidays}
          month={month}
          locked={locked}
          canLock={canLock}
          lockReason={lockReason}
          onLockReason={setLockReason}
          onToggleLock={toggleLock}
          onHoliday={() => setHolidayOpen(true)}
        />
      )}
      <RequestBoard
        title="Regularization requests"
        items={regularizations}
        kind="regularization"
        onDecide={setDecisionTarget}
      />
      <RequestBoard
        title="Overtime approvals"
        items={overtime}
        kind="overtime"
        onDecide={setDecisionTarget}
      />

      <RuleDialog
        open={ruleOpen}
        rule={rule}
        saving={saving}
        onClose={() => setRuleOpen(false)}
        onChange={setRule}
        onSubmit={createRule}
      />
      <HolidayDialog
        open={holidayOpen}
        value={holiday}
        saving={saving}
        onClose={() => setHolidayOpen(false)}
        onChange={setHoliday}
        onSubmit={createHoliday}
      />
      <RegularizationDialog
        open={regularizationOpen}
        value={regularization}
        saving={saving}
        onClose={() => setRegularizationOpen(false)}
        onChange={setRegularization}
        onSubmit={createRegularization}
      />
      <OvertimeDialog
        open={overtimeOpen}
        value={overtimeForm}
        saving={saving}
        onClose={() => setOvertimeOpen(false)}
        onChange={setOvertimeForm}
        onSubmit={createOvertime}
      />
      <DecisionDialog
        target={decisionTarget}
        saving={saving}
        onClose={() => setDecisionTarget(null)}
        onSubmit={decide}
      />
    </div>
  );
}

function RuleRow({ item }) {
  return (
    <div
      className="rounded-md border border-border p-4"
      data-testid={`attendance-rule-${item.id}`}
    >
      <div className="flex justify-between gap-3">
        <div className="font-medium">{item.name}</div>
        {item.is_default && <StatusBadge value="default" />}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
        <span>Grace {item.grace_minutes}m</span>
        <span>Late {item.late_threshold_minutes}m</span>
        <span>Half day {item.half_day_working_minutes}m</span>
        <span>Break {item.break_allowance_minutes}m</span>
      </div>
    </div>
  );
}

function HolidayAndLockPanel({
  holidays,
  month,
  locked,
  canLock,
  lockReason,
  onHoliday,
  onLockReason,
  onToggleLock,
}) {
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Card className="p-5" data-testid="attendance-holiday-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-lg font-semibold">
              Holiday calendar
            </div>
            <div className="text-sm text-muted-foreground">
              Location-aware holidays exclude working days from payroll totals.
            </div>
          </div>
          <Button
            size="sm"
            onClick={onHoliday}
            data-testid="attendance-holiday-create-button"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Add holiday
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {holidays.map((item) => (
            <div
              key={item.id}
              className="flex justify-between gap-4 border-b pb-2 text-sm"
              data-testid={`attendance-holiday-${item.id}`}
            >
              <span className="font-medium">{item.name}</span>
              <span className="text-muted-foreground">
                {item.date} · {item.location}
              </span>
            </div>
          ))}
          {!holidays.length && (
            <div className="text-sm text-muted-foreground">
              No holidays in this month.
            </div>
          )}
        </div>
      </Card>
      <Card className="p-5" data-testid="attendance-lock-card">
        <div className="flex items-center gap-2 font-display text-lg font-semibold">
          <LockKeyhole className="h-5 w-5 text-[#255058]" /> Monthly attendance
          lock
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          {month} is currently <strong>{locked ? "locked" : "open"}</strong>.
          Super Admin overrides remain fully audited.
        </div>
        {canLock && (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label="Reason">
              <Input
                value={lockReason}
                onChange={(event) => onLockReason(event.target.value)}
                data-testid="attendance-lock-reason-input"
              />
            </Field>
            <Button
              onClick={onToggleLock}
              data-testid="attendance-lock-toggle-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              {locked ? "Unlock month" : "Lock month"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function RequestBoard({ items, kind, onDecide, title }) {
  return (
    <Card className="overflow-hidden" data-testid={`attendance-${kind}-board`}>
      <div className="border-b px-5 py-4 font-display text-lg font-semibold">
        {title}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead className="border-b text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-5 py-3">Employee</th>
              <th>Date</th>
              <th>Detail</th>
              <th>Status</th>
              <th className="pr-5">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className="border-b last:border-0"
                data-testid={`attendance-${kind}-row-${item.id}`}
              >
                <td className="px-5 py-3 font-medium">{item.requester_name}</td>
                <td>{item.work_date}</td>
                <td>{kind === "overtime" ? `${item.hours}h` : item.reason}</td>
                <td>
                  <StatusBadge value={item.status} />
                </td>
                <td className="pr-5">
                  {item.status === "pending" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDecide({ ...item, kind })}
                      data-testid={`attendance-${kind}-decide-${item.id}`}
                    >
                      Review
                    </Button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={5} className="px-5 py-6 text-muted-foreground">
                  No requests in your scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RuleDialog({ onChange, onClose, onSubmit, open, rule, saving }) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent data-testid="attendance-rule-dialog">
        <DialogHeader>
          <DialogTitle>Attendance rule</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={rule.name}
              onChange={(event) =>
                onChange({ ...rule, name: event.target.value })
              }
              data-testid="attendance-rule-name-input"
            />
          </Field>
          <Field label="Grace minutes">
            <Input
              type="number"
              value={rule.grace_minutes}
              onChange={(event) =>
                onChange({ ...rule, grace_minutes: Number(event.target.value) })
              }
              data-testid="attendance-rule-grace-input"
            />
          </Field>
          <Field label="Late threshold">
            <Input
              type="number"
              value={rule.late_threshold_minutes}
              onChange={(event) =>
                onChange({
                  ...rule,
                  late_threshold_minutes: Number(event.target.value),
                })
              }
              data-testid="attendance-rule-late-input"
            />
          </Field>
          <Field label="Early logout threshold">
            <Input
              type="number"
              value={rule.early_logout_minutes}
              onChange={(event) =>
                onChange({
                  ...rule,
                  early_logout_minutes: Number(event.target.value),
                })
              }
              data-testid="attendance-rule-early-input"
            />
          </Field>
          <Field label="Half-day minimum work minutes">
            <Input
              type="number"
              value={rule.half_day_working_minutes}
              onChange={(event) =>
                onChange({
                  ...rule,
                  half_day_working_minutes: Number(event.target.value),
                })
              }
              data-testid="attendance-rule-half-day-input"
            />
          </Field>
          <Field label="Break allowance minutes">
            <Input
              type="number"
              value={rule.break_allowance_minutes}
              onChange={(event) =>
                onChange({
                  ...rule,
                  break_allowance_minutes: Number(event.target.value),
                })
              }
              data-testid="attendance-rule-break-input"
            />
          </Field>
        </div>
        <div>
          <Label>Weekly offs</Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
              (day, index) => (
                <label key={day} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={rule.weekly_offs.includes(index)}
                    onChange={(event) => {
                      const weekly_offs = event.target.checked
                        ? [...rule.weekly_offs, index]
                        : rule.weekly_offs.filter((item) => item !== index);
                      onChange({ ...rule, weekly_offs });
                    }}
                    data-testid={`attendance-rule-weekly-off-${index}`}
                  />
                  {day}
                </label>
              ),
            )}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rule.is_default}
            onChange={(event) =>
              onChange({ ...rule, is_default: event.target.checked })
            }
            data-testid="attendance-rule-default-checkbox"
          />{" "}
          Make default policy
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rule.overtime_requires_approval}
            onChange={(event) =>
              onChange({
                ...rule,
                overtime_requires_approval: event.target.checked,
              })
            }
            data-testid="attendance-rule-overtime-checkbox"
          />{" "}
          Require overtime approval
        </label>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="attendance-rule-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !rule.name.trim()}
            data-testid="attendance-rule-save-button"
          >
            Save rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HolidayDialog({ onChange, onClose, onSubmit, open, saving, value }) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="attendance-holiday-dialog">
        <DialogHeader>
          <DialogTitle>Add holiday</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Date">
            <Input
              type="date"
              value={value.date}
              onChange={(event) =>
                onChange({ ...value, date: event.target.value })
              }
              data-testid="attendance-holiday-date-input"
            />
          </Field>
          <Field label="Holiday name">
            <Input
              value={value.name}
              onChange={(event) =>
                onChange({ ...value, name: event.target.value })
              }
              data-testid="attendance-holiday-name-input"
            />
          </Field>
          <Field label="Location">
            <Input
              value={value.location}
              onChange={(event) =>
                onChange({ ...value, location: event.target.value })
              }
              data-testid="attendance-holiday-location-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="attendance-holiday-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !value.name || !value.date}
            data-testid="attendance-holiday-save-button"
          >
            Add holiday
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegularizationDialog({
  onChange,
  onClose,
  onSubmit,
  open,
  saving,
  value,
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="attendance-regularization-dialog">
        <DialogHeader>
          <DialogTitle>Regularize attendance</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Work date">
            <Input
              type="date"
              value={value.work_date}
              onChange={(event) =>
                onChange({ ...value, work_date: event.target.value })
              }
              data-testid="attendance-regularization-date-input"
            />
          </Field>
          <Field label="Proposed login">
            <Input
              type="datetime-local"
              value={value.proposed_login_at}
              onChange={(event) =>
                onChange({ ...value, proposed_login_at: event.target.value })
              }
              data-testid="attendance-regularization-login-input"
            />
          </Field>
          <Field label="Proposed logout">
            <Input
              type="datetime-local"
              value={value.proposed_logout_at}
              onChange={(event) =>
                onChange({ ...value, proposed_logout_at: event.target.value })
              }
              data-testid="attendance-regularization-logout-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="attendance-regularization-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="attendance-regularization-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !value.work_date || !value.reason.trim()}
            data-testid="attendance-regularization-submit-button"
          >
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OvertimeDialog({ onChange, onClose, onSubmit, open, saving, value }) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="attendance-overtime-dialog">
        <DialogHeader>
          <DialogTitle>Request overtime</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Work date">
            <Input
              type="date"
              value={value.work_date}
              onChange={(event) =>
                onChange({ ...value, work_date: event.target.value })
              }
              data-testid="attendance-overtime-date-input"
            />
          </Field>
          <Field label="Hours">
            <Input
              type="number"
              min="0.25"
              step="0.25"
              value={value.hours}
              onChange={(event) =>
                onChange({ ...value, hours: event.target.value })
              }
              data-testid="attendance-overtime-hours-input"
            />
          </Field>
          <Field label="Reason">
            <Textarea
              value={value.reason}
              onChange={(event) =>
                onChange({ ...value, reason: event.target.value })
              }
              data-testid="attendance-overtime-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="attendance-overtime-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              saving || !value.work_date || !value.hours || !value.reason.trim()
            }
            data-testid="attendance-overtime-submit-button"
          >
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ onClose, onSubmit, saving, target }) {
  const [form, setForm] = useState({
    decision: "approved",
    comment: "",
    reason: "",
  });
  useEffect(
    () => setForm({ decision: "approved", comment: "", reason: "" }),
    [target],
  );
  if (!target) return null;
  return (
    <Dialog open={Boolean(target)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="attendance-decision-dialog">
        <DialogHeader>
          <DialogTitle>Review {target.kind} request</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Decision">
            <Select
              value={form.decision}
              onValueChange={(decision) => setForm({ ...form, decision })}
            >
              <SelectTrigger data-testid="attendance-decision-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approve</SelectItem>
                <SelectItem value="rejected">Reject</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Comment">
            <Textarea
              value={form.comment}
              onChange={(event) =>
                setForm({ ...form, comment: event.target.value })
              }
              data-testid="attendance-decision-comment-input"
            />
          </Field>
          <Field label="Override reason (required for locked months)">
            <Textarea
              value={form.reason}
              onChange={(event) =>
                setForm({ ...form, reason: event.target.value })
              }
              data-testid="attendance-decision-reason-input"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="attendance-decision-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(form, target)}
            disabled={saving}
            data-testid="attendance-decision-submit-button"
          >
            Save decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ children, label }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
